import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, Button, Box, IconButton, Tooltip,
} from '@material-ui/core';
import CropSquareIcon from '@material-ui/icons/CropSquare';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import EditLocationIcon from '@material-ui/icons/EditLocation';

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: 500,
    flex: 1,
  },
  row: {
    padding: '10px 12px',
    background: '#f5f5f5',
    borderRadius: 6,
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 13,
  },
  rowLabel: {
    flex: 1,
  },
  rowMeta: {
    fontSize: 11,
    color: '#999',
    marginTop: 2,
  },
  drawBtn: {
    textTransform: 'none',
    fontSize: 12,
    padding: '4px 10px',
  },
  customLine: {
    padding: '6px 10px',
    borderLeft: '3px dashed #2e7d32',
    marginBottom: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  customName: {
    flex: 1,
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  emptyBox: {
    padding: 16,
    background: '#f5f5f5',
    borderRadius: 6,
    textAlign: 'center',
    fontSize: 12,
    color: '#999',
  },
  fallbackChip: {
    fontSize: 10,
    padding: '2px 6px',
    background: '#FEF3E2',
    color: '#B45309',
    borderRadius: 4,
    marginLeft: 6,
  },
  hiddenRow: {
    opacity: 0.5,
  },
}));

const PropertyLinesSection = ({
  countyLines = [],
  manualLines = [],
  usingFallback,
  countyVisible,
  manualVisible,
  onToggleCounty,
  onToggleManual,
  onDrawCustom,
  onEditManualShape,
  onDeleteManual,
  onRenameManual,
  drawing,
  editing,
  disabled,
}) => {
  const classes = useStyles();

  return (
    <Box>
      <div className={classes.header}>
        <CropSquareIcon style={{ fontSize: 18, color: '#666' }} />
        <Typography className={classes.title}>Property lines</Typography>
      </div>

      {/* County parcel row */}
      <div className={`${classes.row} ${!countyVisible ? classes.hiddenRow : ''}`}>
        <div className={classes.rowLabel}>
          <div>County parcel</div>
          <div className={classes.rowMeta}>
            {countyLines.length > 0
              ? `${countyLines.length} loaded`
              : 'None found'}
            {usingFallback && <span className={classes.fallbackChip}>Fallback</span>}
          </div>
        </div>
        {countyLines.length > 0 && (
          <Tooltip title={countyVisible ? 'Hide county lines' : 'Show county lines'}>
            <IconButton size="small" onClick={onToggleCounty} style={{ padding: 4 }}>
              {countyVisible
                ? <VisibilityIcon style={{ fontSize: 18, color: '#4caf50' }} />
                : <VisibilityOffIcon style={{ fontSize: 18, color: '#999' }} />}
            </IconButton>
          </Tooltip>
        )}
      </div>

      {/* Custom boundary row */}
      <div className={`${classes.row} ${!manualVisible ? classes.hiddenRow : ''}`}>
        <div className={classes.rowLabel}>
          <div>Custom boundary</div>
          <div className={classes.rowMeta}>
            {manualLines.length > 0
              ? `${manualLines.length} drawn`
              : 'Draw your own override'}
          </div>
        </div>
        {manualLines.length > 0 && (
          <Tooltip title={manualVisible ? 'Hide custom lines' : 'Show custom lines'}>
            <IconButton size="small" onClick={onToggleManual} style={{ padding: 4 }}>
              {manualVisible
                ? <VisibilityIcon style={{ fontSize: 18, color: '#2e7d32' }} />
                : <VisibilityOffIcon style={{ fontSize: 18, color: '#999' }} />}
            </IconButton>
          </Tooltip>
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={onDrawCustom}
          className={classes.drawBtn}
          disabled={disabled || drawing}
        >
          {drawing ? 'Drawing...' : 'Draw'}
        </Button>
      </div>

      {/* List of custom lines */}
      {manualLines.length > 0 && (
        <Box mt={1}>
          {manualLines.map((line) => (
            <div key={line._id} className={classes.customLine}>
              <span className={classes.customName}>{line.name || 'Custom'}</span>
              <Tooltip title="Edit shape">
                <IconButton
                  size="small"
                  onClick={() => onEditManualShape(line)}
                  style={{ padding: 2 }}
                  disabled={editing || drawing}
                >
                  <EditLocationIcon style={{ fontSize: 14, color: '#ff9800' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rename">
                <IconButton
                  size="small"
                  onClick={() => {
                    const newName = window.prompt('Rename property line:', line.name || '');
                    if (newName && newName.trim()) onRenameManual(line._id, newName.trim());
                  }}
                  style={{ padding: 2 }}
                >
                  <EditIcon style={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (window.confirm('Delete this custom property line?')) {
                      onDeleteManual(line._id);
                    }
                  }}
                  style={{ padding: 2 }}
                >
                  <DeleteIcon style={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </div>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PropertyLinesSection;
